import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import {
  AiChatActions,
  AiChatService,
  DocumentsStore,
  selectChatIdByClientTag,
  selectCreatedChatIdByMysid,
  selectEventsByChatId,
  UserStore,
} from '@qtek/libs/store';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import { WsErrorSuppressionService } from '@qtek/libs/ws-error-handler';
import {
  AiChatEvent,
  ChatStatusPayload,
  ChatStatusPendingApprovalPayload,
  ChatStreamPayload,
  DocNode,
  isPendingApprovalStatus,
  PUBLIC_ACCESS,
} from '@qtek/shared/models';
import {
  ALLOWED_DOCUMENT_ATTACHMENT_ACCEPT,
  ALLOWED_DOCUMENT_ATTACHMENT_FORMAT_ERROR_MESSAGE,
  isAllowedDocumentAttachment,
} from '@qtek/shared/utils';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  take,
  takeUntil,
} from 'rxjs/operators';
import {
  DynamicActionCardPayload,
  isDynamicActionCardPayload,
} from '../../models/action-card.types';
import { Author, ChatMsgVM } from '../../models/message.types';
import { AiChatEventSubscriptionService } from '../../services/ai-chat-event-subscription.service';
import {
  AiChatApprovalCardComponent,
  AiChatEnhancedMessageBubbleComponent,
} from '../messages';

interface PendingMessage {
  id: string;
  text: string;
  at: number;
  attachmentCount: number;
}

/** SD-2326: an in-flight propose-action card awaiting the user's accept/reject. */
interface PendingApprovalVM {
  pendingApprovalId: string;
  payload: DynamicActionCardPayload;
}

const PENDING_TIMESTAMP_TOLERANCE = 5000;

type SubscribeChatsPayload = Parameters<
  typeof AiChatActions.subscribeChats
>[0]['payload'];
type SubscribeChatEventsPayload = Parameters<
  typeof AiChatActions.subscribeChatEvents
>[0]['payload'];
type CreateChatPayload = Parameters<
  typeof AiChatActions.createChat
>[0]['payload'];
type SendMessagePayload = Parameters<
  typeof AiChatActions.sendMessage
>[0]['payload'];

@Component({
  selector: 'qt-ai-chat',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    TranslationCoreModule,
    AiChatApprovalCardComponent,
    AiChatEnhancedMessageBubbleComponent,
  ],
  templateUrl: './ai-chat.component.html',
  styleUrls: ['./ai-chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Accept attribute for the file input (allowed document + image types). */
  readonly allowedAttachmentAccept = ALLOWED_DOCUMENT_ATTACHMENT_ACCEPT;

  /** Inline error when user selects a disallowed file format (shown above input). */
  readonly attachmentFormatError = signal<string | null>(null);

  // Signal-based inputs
  readonly windowId = input.required<string>();
  readonly chatId = input<string | null>();
  readonly lazySubscribe = input(false);
  readonly typingLottieSrc = input<string | undefined>(
    'assets/animations/allo.lottie'
  );
  readonly typingLottieSize = input(48);

  @ViewChild('scroll', { static: true }) scroll!: ElementRef<HTMLElement>;
  @ViewChild('inputArea', { static: false })
  inputArea?: ElementRef<HTMLElement>;
  @ViewChild('filePicker', { static: false })
  filePicker?: ElementRef<HTMLInputElement>;
  @ViewChild('chatInput', { static: false })
  chatInput?: ElementRef<HTMLTextAreaElement>;

  /** Attached files Claude-style */
  readonly attachedFiles = signal<{ id: string; name: string; type: string }[]>(
    []
  );
  /** Pending uploads (name + type) shown until doc is resolved. */
  readonly pendingFilesForDisplay = signal<{ name: string; type: string }[]>(
    []
  );

  // Signals for reactive state management
  readonly draft = signal('');
  readonly attachedDocIds = signal<string[]>([]);
  readonly pendingUploads = signal(0);
  readonly isTyping = signal(false);
  readonly messages = signal<ChatMsgVM[]>([]);
  readonly pendingMessages = signal<ChatMsgVM[]>([]);
  readonly currentChatId = signal<string | null>(null);
  readonly isDropActive = signal(false);

  readonly toolStatusLabel = signal<string | null>(null);

  /** SD-2326: dynamic approval cards waiting on the user's decision. */
  readonly pendingApprovals = signal<PendingApprovalVM[]>([]);

  /**
   * SD-2155: in-flight bubble assembled from `chatStream` deltas while Allo
   * is generating. Keyed by `chatId+ts`; cleared once the final {@link AiChatEvent}
   * lands with a matching ts (see remoteMessages effect). `null` when no stream
   * is active for the current chat.
   */
  readonly streamingBubble = signal<ChatMsgVM | null>(null);

  // Computed signals
  readonly allMessages = computed(() => {
    const remote = this.remoteMessages();
    const pending = this.pendingMessages();
    const stream = this.streamingBubble();
    // Order matters during a live stream:
    //
    //   remote  → already-committed history (older user/bot turns)
    //   pending → the user message that triggered THIS turn — still
    //             optimistic-only because the BE batches the user-msg
    //             insert + bot-msg insert into a single Kafka transaction
    //             that doesn't broadcast until the entire AI response
    //             completes. So during streaming, `pending` is the only
    //             place the just-sent user message lives.
    //   stream  → the bot bubble being assembled from chatStream deltas,
    //             i.e. the reply TO whatever's in `pending`. Must render
    //             AFTER it.
    //
    // Once the BE transaction commits, the user msg moves pending→remote,
    // the bot msg also lands in remote, and reconcileStreamingBubble drops
    // the stream entry. Final order resolves to plain [...remote] cleanly.
    return stream ? [...remote, ...pending, stream] : [...remote, ...pending];
  });

  /**
   * SD-2450: gate the "thinking" typing indicator off while an interrupted/
   * errored bubble is showing. The turn is over (the bubble carries the Retry
   * affordance), so the typing lottie must not linger even if the typing-state
   * reconcile briefly flips {@link isTyping} back on.
   */
  readonly showTyping = computed(
    () => this.isTyping() && !this.streamingBubble()?.errored
  );

  readonly canAttach = computed(
    () => this.attachedDocIds().length + this.pendingUploads() < 8
  );

  /**
   * SD-2326: an approval card is open. Only one propose_action interrupt can be
   * paused per chat (single LangGraph checkpoint), so a new message would just
   * be rejected by the BE — block sending and surface a hint until it's resolved.
   */
  readonly hasPendingApproval = computed(
    () => this.pendingApprovals().length > 0
  );

  readonly canSend = computed(() => {
    const draftText = this.draft().trim();
    return (
      draftText.length > 0 &&
      this.pendingUploads() === 0 &&
      !this.hasPendingApproval()
    );
  });

  getPreviousMessageText(index: number): string {
    const list = this.allMessages();
    const prev = list[index - 1];
    return prev?.author === 'user' ? (prev.text ?? '') : '';
  }

  readonly typingDots = [0, 1, 2];
  readonly typingMessage: ChatMsgVM = {
    id: 'typing',
    text: '',
    author: 'bot',
    at: Date.now(),
  };

  // Private state
  private sendQueued = false;
  private queuedDraft: string | null = null;
  private pendingFileInfos = new Map<string, { type: string }>();
  private attachmentNamesByMessageKey = new Map<string, string[]>();
  private destroy$ = new Subject<void>();
  private chatId$ = new BehaviorSubject<string | null>(null);
  private typingSubject = new BehaviorSubject<boolean>(false);
  private lastPendingUserTs: number | null = null;
  /** SD-2450: last prompt sent, replayed verbatim by {@link retryLastPrompt}. */
  private lastUserPrompt: { text: string; docIds: string[] } | null = null;
  private pendingMsgsSubject = new BehaviorSubject<ChatMsgVM[]>([]);
  private pendingQueue: PendingMessage[] = [];
  private hasSubscribedToChats = false;
  private dragCounter = 0;
  private activeChatSubscriptionId: string | null = null;
  private focusObserver: IntersectionObserver | null = null;
  private wasInputIntersecting = false;

  // Signal-based state
  private readonly resolvedChatId = signal<string | null>(null);
  private readonly remoteMessages = signal<ChatMsgVM[]>([]);

  // Signal-based injections
  private readonly store = inject(Store);
  private readonly userStore = inject(UserStore);
  private readonly documentsStore = inject(DocumentsStore);
  private readonly injector = inject(Injector);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly chatEventSubscriptions = inject(
    AiChatEventSubscriptionService
  );
  private readonly chatSvc = inject(AiChatService);
  private readonly wsErrorSuppression = inject(WsErrorSuppressionService);

  constructor() {
    // Effect to sync typing state
    effect(() => {
      const typing = this.isTyping();
      this.typingSubject.next(typing);
    });

    // Effect to sync messages
    effect(() => {
      this.allMessages();
      untracked(() => this.scrollToBottomSoon());
    });

    // Effect to resolve chat ID from multiple sources
    effect(() => {
      const windowId = this.windowId();
      const chatIdFromInput = this.chatId();

      // Subscribe to store selectors and resolve chat ID
      const chatIdFromMysid$ = this.store.select(
        selectCreatedChatIdByMysid(`chats_${windowId}`)
      ) as Observable<string | null>;

      const chatIdFromTag$ = this.store.select(
        selectChatIdByClientTag(windowId)
      ) as Observable<string | null>;

      combineLatest([
        of(chatIdFromInput ?? null),
        chatIdFromMysid$,
        chatIdFromTag$,
      ])
        .pipe(
          map(
            ([fromInput, fromMysid, fromTag]) =>
              fromInput || fromMysid || fromTag || null
          ),
          distinctUntilChanged(),
          takeUntil(this.destroy$)
        )
        .subscribe((resolvedId: string | null) => {
          const prev = this.chatId$.value;
          if (prev !== resolvedId) {
            this.clearAllPending();
          }
          this.chatId$.next(resolvedId);
          this.currentChatId.set(resolvedId);
          this.resolvedChatId.set(resolvedId);
        });
    });

    // Effect to handle remote messages based on resolved chat ID
    effect(() => {
      const chatId = this.resolvedChatId();

      if (!chatId) {
        this.releaseChatEventSubscription();
        this.remoteMessages.set([]);
        return;
      }

      // Subscribe to chat events for this chat ID (ref-counted)
      this.ensureChatEventSubscription(chatId);

      // Subscribe to events for this chat
      (
        this.store.select(selectEventsByChatId(chatId)) as Observable<
          AiChatEvent[]
        >
      )
        .pipe(
          map(events => (events ?? []).map(event => this.toChatMessage(event))),
          takeUntil(this.destroy$)
        )
        .subscribe(messages => {
          const enhanced = messages.map(msg => ({ ...msg }));
          this.reconcilePending(enhanced);
          this.remoteMessages.set(enhanced);
          // SD-2155: drop the in-flight streaming bubble once the canonical
          // chatEvent with matching ts lands — that's the source of truth
          // now, no point double-rendering.
          this.reconcileStreamingBubble(enhanced);
          this.cdr.markForCheck();

          if (!enhanced.length) {
            if (!this.pendingQueue.length) {
              this.isTyping.set(false);
              this.toolStatusLabel.set(null);
              this.lastPendingUserTs = null;
            } else {
              this.isTyping.set(true);
            }
            return;
          }

          if (this.lastPendingUserTs !== null) {
            for (let i = enhanced.length - 1; i >= 0; i--) {
              const candidate = enhanced[i];
              if (candidate.author === 'bot') {
                if (candidate.at >= this.lastPendingUserTs) {
                  this.isTyping.set(false);
                  this.toolStatusLabel.set(null);
                  this.lastPendingUserTs = null;
                }
                break;
              }
            }
          }
        });
    });

    this.chatSvc.chatStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe((status: ChatStatusPayload) => {
        const activeChatId = this.currentChatId();
        if (!activeChatId || status.chatId !== activeChatId) return;

        if (isPendingApprovalStatus(status)) {
          this.handlePendingApproval(status);
          return;
        }

        this.toolStatusLabel.set(status.label);
        this.cdr.markForCheck();
      });

    // SD-2155: assemble the in-flight assistant bubble from BE token deltas.
    // The bubble sits in allMessages between remote and pending, gets text
    // appended on each delta, and reconciles against the final chatEvent
    // (same ts) when it arrives via remoteMessages effect.
    this.chatSvc.chatStream$
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload: ChatStreamPayload) => {
        const activeChatId = this.currentChatId();
        if (!activeChatId || payload.chatId !== activeChatId) return;
        this.handleChatStream(payload);
      });
  }

  /**
   * SD-2155: per-event handler for the chatStream WS subscription. Branches
   * on `type`:
   *
   * - `start`    — create a fresh streaming bubble keyed by chatId+ts. Don't
   *                clear the typing indicator yet; we're still waiting for
   *                first content.
   * - `delta`    — append `delta` text to the streaming bubble. First delta
   *                also flips off the typing indicator and tool-status label
   *                because we're now visibly rendering, not "thinking".
   * - `complete` — mark the bubble as no-longer-streaming; final chatEvent
   *                will arrive shortly and replace it via reconcile.
   */
  private handleChatStream(payload: ChatStreamPayload): void {
    const { chatId, ts, type, delta } = payload;
    if (type === 'start') {
      const bubble: ChatMsgVM = {
        id: `stream_${chatId}_${ts}`,
        chatId,
        ts,
        at: ts,
        text: '',
        author: 'bot',
        streaming: true,
      };
      this.streamingBubble.set(bubble);
      this.cdr.markForCheck();
      return;
    }

    if (type === 'delta') {
      const current = this.streamingBubble();
      // Defensive: if `start` was missed (page reloaded mid-stream, etc.),
      // bootstrap the bubble lazily off the first delta we see.
      const base: ChatMsgVM = current ?? {
        id: `stream_${chatId}_${ts}`,
        chatId,
        ts,
        at: ts,
        text: '',
        author: 'bot',
        streaming: true,
      };
      this.streamingBubble.set({
        ...base,
        text: base.text + (delta ?? ''),
      });
      // First visible token → stop showing "Agents thinking..." / tool spinner.
      if (this.isTyping()) {
        this.isTyping.set(false);
        this.toolStatusLabel.set(null);
      }
      this.cdr.markForCheck();
      return;
    }

    if (type === 'complete') {
      const current = this.streamingBubble();
      if (current && current.ts === ts) {
        // Keep the assembled text visible but flip off the streaming flag so
        // feedback/tools-summary UI shows immediately. The final chatEvent will
        // land shortly and the remoteMessages effect will swap this bubble out.
        this.streamingBubble.set({ ...current, streaming: false });
        this.cdr.markForCheck();
      }
      return;
    }

    if (type === 'error') {
      // SD-2450: the turn failed (mid-stream drop or other). Flip the bubble into
      // an interrupted state — keep whatever partial text arrived but stop the
      // spinner and render the retry affordance. No final chatEvent will land, so
      // the bubble stays put until the user retries (which clears it).
      const current = this.streamingBubble();
      const base: ChatMsgVM = current ?? {
        id: `stream_${chatId}_${ts}`,
        chatId,
        ts,
        at: ts,
        text: '',
        author: 'bot',
      };
      this.streamingBubble.set({
        ...base,
        streaming: false,
        errored: true,
        errorCode: payload.code ?? 'failed',
      });
      this.isTyping.set(false);
      this.toolStatusLabel.set(null);
      // The failed turn is now surfaced inline here; suppress the redundant
      // global "Internal error" dialog the rethrown failure would otherwise raise.
      this.wsErrorSuppression.suppressNextChatEventError();
      this.cdr.markForCheck();
      return;
    }
  }

  /**
   * SD-2450: re-issue the last user prompt after an interrupted/failed turn.
   * Clears the errored streaming bubble and re-sends the captured text +
   * attachments through the normal send path. No-op if there's nothing to retry.
   */
  retryLastPrompt(): void {
    const last = this.lastUserPrompt;
    if (!last) return;
    this.streamingBubble.set(null);
    this.attachedDocIds.set([...last.docIds]);
    this.sendInternal(last.text).catch(console.error);
  }

  /**
   * SD-2326: parse the cardJson payload off a `pending_approval` chatStatus
   * event and push it onto the in-flight list so the template renders an
   * approval card below the chat stream. Defensive — bad/empty cardJson is
   * logged and dropped rather than crashing the chat thread.
   */
  private handlePendingApproval(
    status: ChatStatusPendingApprovalPayload
  ): void {
    let card: unknown;
    try {
      card = JSON.parse(status.cardJson);
    } catch (e) {
      console.warn('Failed to parse pending_approval cardJson', e);
      return;
    }
    if (!isDynamicActionCardPayload(card)) {
      console.warn(
        'pending_approval cardJson did not match DynamicActionCardPayload shape',
        card
      );
      return;
    }
    this.pendingApprovals.update(arr => [
      ...arr,
      { pendingApprovalId: status.pendingApprovalId, payload: card },
    ]);
    this.toolStatusLabel.set(null);
    this.isTyping.set(false);
  }

  /** SD-2326: remove a resolved approval card from the in-flight list. */
  protected onApprovalResolved(pendingApprovalId: string): void {
    this.pendingApprovals.update(arr =>
      arr.filter(pa => pa.pendingApprovalId !== pendingApprovalId)
    );
  }

  ngOnInit(): void {
    const chatIdValue = this.chatId();
    if (chatIdValue) this.chatId$.next(chatIdValue);

    if (!this.lazySubscribe()) {
      this.ensureChatSubscription();
    }

    this.setupDocWatcher();
  }

  ngAfterViewInit(): void {
    const el = this.chatInput?.nativeElement;
    if (!el) return;
    this.focusObserver = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry) return;
        const isVisible = entry.isIntersecting;
        if (isVisible && !this.wasInputIntersecting) {
          el.focus();
        }
        this.wasInputIntersecting = isVisible;
      },
      { threshold: 0.1 }
    );
    this.focusObserver.observe(el);
  }

  onChatAreaClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest('a, button, [role="button"], input, textarea')) return;
    this.focusChatInput();
  }

  private focusChatInput(): void {
    this.chatInput?.nativeElement?.focus();
  }

  private setupDocWatcher(): void {
    toObservable(this.documentsStore.personalDocuments, {
      injector: this.injector,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe((list: DocNode[] | null) => {
        const docs = list ?? [];
        const docsReversed = [...docs].reverse();
        let mutated = false;
        for (const doc of docsReversed) {
          const key = doc.fileName ?? doc.desc ?? doc.origfl;
          const id = doc._id;
          if (!key || !id) continue;

          let matchedPendingKey: string | null = key;
          let pending = this.pendingFileInfos.get(key);
          if (!pending && this.pendingFileInfos.size > 0) {
            const pendingNames = [...this.pendingFileInfos.keys()];
            const fallback = pendingNames.find(
              n =>
                key.endsWith(n) ||
                n.endsWith(key) ||
                key.includes(n) ||
                n.includes(key)
            );
            if (fallback) {
              pending = this.pendingFileInfos.get(fallback)!;
              matchedPendingKey = fallback;
            } else {
              matchedPendingKey = null;
            }
          }
          if (
            pending &&
            matchedPendingKey &&
            !this.attachedDocIds().includes(id)
          ) {
            this.attachedDocIds.update(ids => [...ids, id]);
            this.attachedFiles.update(files => [
              ...files,
              { id, name: key, type: pending!.type },
            ]);
            this.pendingFileInfos.delete(matchedPendingKey);
            this.pendingFilesForDisplay.update(list =>
              list.filter(f => f.name !== matchedPendingKey)
            );
            this.pendingUploads.update(count => Math.max(0, count - 1));
            mutated = true;
          }
        }
        if (mutated) {
          this.cdr.markForCheck();
        }
        this.checkAutoSendAfterUploads();
      });
  }

  ngOnDestroy(): void {
    this.focusObserver?.disconnect();
    this.focusObserver = null;
    this.store.dispatch(
      AiChatActions.unsubscribeChats({ mysid: `chats_${this.windowId()}` })
    );
    this.releaseChatEventSubscription();

    this.destroy$.next();
    this.destroy$.complete();
    this.typingSubject.complete();
    this.pendingMsgsSubject.complete();
  }

  openFilePicker(): void {
    this.filePicker?.nativeElement?.click();
  }

  private readonly chatInputMinHeight = 32;

  growChatInput(): void {
    const el = this.chatInput?.nativeElement;
    if (!el) return;
    const isEmpty = !el.value.trim();
    if (isEmpty) {
      el.style.height = `${this.chatInputMinHeight}px`;
      return;
    }
    el.style.height = '0';
    const newHeight = Math.max(
      this.chatInputMinHeight,
      Math.min(el.scrollHeight, 200)
    );
    el.style.height = `${newHeight}px`;
  }

  onChatKeydown(event: Event): void {
    const e = event as KeyboardEvent;
    if (e.key !== 'Enter') return;
    if (e.shiftKey) return;
    e.preventDefault();
    this.send();
  }

  fileTypeLabel(type: string): string {
    if (!type) return '';
    const mimeMap: Record<string, string> = {
      'application/msword': 'DOC',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        'DOCX',
      'application/vnd.ms-excel': 'XLS',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        'XLSX',
      'application/vnd.ms-powerpoint': 'PPT',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        'PPTX',
      'text/plain': 'TXT',
      'text/markdown': 'MD',
    };
    if (mimeMap[type]) return mimeMap[type];
    const part = type.split('/').pop() ?? type;
    return part.toUpperCase();
  }

  removeAttachedFile(docId: string): void {
    this.attachedDocIds.update(ids => ids.filter(id => id !== docId));
    this.attachedFiles.update(files => files.filter(f => f.id !== docId));
  }

  removePendingFile(fileName: string): void {
    this.pendingFileInfos.delete(fileName);
    this.pendingFilesForDisplay.update(list =>
      list.filter(f => f.name !== fileName)
    );
    this.pendingUploads.update(count => Math.max(0, count - 1));
    this.cdr.markForCheck();
  }

  onFilesSelected(evt: Event): void {
    const input = evt.target as HTMLInputElement;
    const files = input?.files ? Array.from(input.files) : [];
    this.uploadFiles(files, () => {
      if (input) input.value = '';
    });
  }

  onDragEnter(event: DragEvent): void {
    if (!this.hasFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter++;
    this.isDropActive.set(true);
  }

  onDragOver(event: DragEvent): void {
    if (!this.hasFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
  }

  onDragLeave(event: DragEvent): void {
    if (!this.hasFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    const container = this.inputArea?.nativeElement;
    const related = event.relatedTarget as Node | null;
    const stillInside = container && related && container.contains(related);
    if (!stillInside) {
      this.dragCounter = Math.max(0, this.dragCounter - 1);
      if (this.dragCounter === 0) {
        this.isDropActive.set(false);
      }
    }
  }

  onDrop(event: DragEvent): void {
    if (!this.hasFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    const files = Array.from(event.dataTransfer?.files ?? []);
    this.dragCounter = 0;
    this.isDropActive.set(false);
    setTimeout(() => {
      this.uploadFiles(files);
    }, 0);
  }

  private checkAutoSendAfterUploads(): void {
    if (this.pendingUploads() === 0 && this.sendQueued) {
      const text = (this.queuedDraft || '').trim();
      this.sendQueued = false;
      this.queuedDraft = null;
      if (text || this.attachedDocIds().length) {
        this.sendInternal(text).catch(console.error);
      }
    }
  }

  // Ensure we have a chatId - signal-based approach
  private async ensureChatId(): Promise<string> {
    this.ensureChatSubscription();

    const currentId = this.chatId$.value;
    if (currentId) return currentId;

    const mysid = `chats_${this.windowId()}`;
    const payload: CreateChatPayload = {
      res: {},
      s: 'model',
      view: 'pln',
    };
    this.store.dispatch(AiChatActions.createChat({ payload }));

    return new Promise<string>(resolve => {
      (
        this.store.select(selectCreatedChatIdByMysid(mysid)) as Observable<
          string | null
        >
      )
        .pipe(
          filter((x): x is string => !!x),
          take(1)
        )
        .subscribe(newId => {
          this.chatId$.next(newId);
          resolve(newId);
        });
    });
  }

  // Build and dispatch the chatEvent insert with documentIds - signal-based approach
  private async sendInternal(text: string): Promise<void> {
    const chatId = await this.ensureChatId();

    // SD-2450: remember this prompt verbatim so an interrupted turn can be retried.
    this.lastUserPrompt = { text, docIds: [...this.attachedDocIds()] };

    const event: AiChatEvent = {
      chatId,
      data: text,
      src: 'user',
      ts: Date.now(),
    };

    if (this.attachedDocIds().length) {
      event.documentIds = [...this.attachedDocIds()];
    }

    const payload: SendMessagePayload = {
      id: chatId,
      res: event,
      s: 'model',
      view: 'pln',
    };

    this.store.dispatch(AiChatActions.sendMessage({ payload }));
    const timestamp = event.ts ?? Date.now();
    this.lastPendingUserTs = timestamp;
    this.isTyping.set(true);
    this.toolStatusLabel.set('Agents thinking...');
    const names = this.attachedFiles().map(f => f.name);
    if (names.length) {
      this.attachmentNamesByMessageKey.set(`${chatId}_${timestamp}`, names);
    }
    this.addPendingMessage(text, timestamp);

    // reset input state
    this.draft.set('');
    this.attachedDocIds.set([]);
    this.attachedFiles.set([]);
    this.pendingFilesForDisplay.set([]);
    const inputEl = this.chatInput?.nativeElement;
    if (inputEl) inputEl.style.height = `${this.chatInputMinHeight}px`;
    this.scrollToBottomSoon();
  }

  // send handler - signal-based approach
  send(): void {
    const text = this.draft()?.trim() ?? '';
    const docIds = this.attachedDocIds();
    const pending = this.pendingUploads();
    if (!text && !docIds.length) return;

    if (pending > 0 && docIds.length === 0) {
      this.sendQueued = true;
      this.queuedDraft = text;
      return;
    }
    this.attachmentFormatError.set(null);
    this.sendInternal(text).catch(console.error);
  }

  private scrollToBottomSoon() {
    queueMicrotask(() => {
      const el = this.scroll?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }

  private ensureChatSubscription(): void {
    if (this.hasSubscribedToChats) return;

    const mysid = `chats_${this.windowId()}`;
    const payload: SubscribeChatsPayload = {
      mysid,
      s: 'model',
      view: 'pln',
    };
    this.store.dispatch(AiChatActions.subscribeChats({ payload }));
    this.hasSubscribedToChats = true;
  }

  private toChatMessage(event: AiChatEvent): ChatMsgVM {
    const at = event.ts ?? Date.now();
    const author: Author = event.src === 'user' ? 'user' : 'bot';
    const text = event.data ?? '';
    const attachmentCount = event.documentIds?.length ?? 0;

    const msg: ChatMsgVM = {
      id: `${event.chatId}_${at}`,
      text,
      author,
      at,
      attachmentCount,
      chatId: event.chatId,
      ts: event.ts,
    };

    if (author === 'user' && attachmentCount > 0) {
      const cached = this.attachmentNamesByMessageKey.get(
        `${event.chatId}_${at}`
      );
      if (cached?.length) {
        msg.attachmentNames = cached;
      }
    }

    if (event.toolsUsed?.length) {
      msg.toolsUsed = event.toolsUsed;
    }

    return msg;
  }

  private ensureChatEventSubscription(chatId: string): void {
    if (this.activeChatSubscriptionId === chatId) return;

    this.releaseChatEventSubscription();

    const mysid = `chatEvent_${chatId}`;
    const payload: SubscribeChatEventsPayload = {
      mysid,
      q: [{ k: 'chatId', op: 'EQ', v: [chatId] }],
      s: 'model',
      view: 'pln',
    };

    this.chatEventSubscriptions.acquire(chatId, payload);
    this.activeChatSubscriptionId = chatId;
  }

  private releaseChatEventSubscription(): void {
    if (!this.activeChatSubscriptionId) return;
    this.chatEventSubscriptions.release(this.activeChatSubscriptionId);
    this.activeChatSubscriptionId = null;
  }

  private addPendingMessage(text: string, at: number): void {
    const id = `pending_${at}_${Math.random().toString(16).slice(2)}`;
    const attachmentCount = this.attachedDocIds().length;
    const attachmentNames = this.attachedFiles().map(f => f.name);
    const message: ChatMsgVM = {
      id,
      text,
      author: 'user',
      at,
      attachmentCount,
      attachmentNames,
    };

    this.pendingQueue.push({
      id,
      text: text.trim(),
      at,
      attachmentCount,
    });
    this.pendingMessages.update(messages => [...messages, message]);
    this.cdr.markForCheck();
  }

  /**
   * SD-2155: clear the in-flight streaming bubble once the canonical
   * chatEvent with matching ts lands. The remote message is the source of
   * truth (it's been through the BE output guardrail + DDB persistence) and
   * the streaming bubble was always a wire-only preview.
   */
  private reconcileStreamingBubble(remote: ChatMsgVM[]): void {
    const stream = this.streamingBubble();
    if (!stream || stream.ts == null) return;
    const matched = remote.some(
      msg => msg.author === 'bot' && msg.ts === stream.ts
    );
    if (matched) {
      this.streamingBubble.set(null);
    }
  }

  private reconcilePending(remote: ChatMsgVM[]): void {
    if (!this.pendingQueue.length) return;

    const remoteUser = remote.filter(msg => msg.author === 'user');
    if (!remoteUser.length) return;

    let pendingList = this.pendingMessages();
    let changed = false;

    this.pendingQueue = this.pendingQueue.filter(pending => {
      const match = remoteUser.find(msg => {
        const messageAt = msg.at ?? 0;
        const timeDiff = Math.abs(messageAt - pending.at);
        const withinWindow =
          messageAt >= pending.at || timeDiff <= PENDING_TIMESTAMP_TOLERANCE;
        return withinWindow && msg.text.trim() === pending.text.trim();
      });

      if (match) {
        if (
          pending.attachmentCount > 0 &&
          (!match.attachmentCount ||
            match.attachmentCount < pending.attachmentCount)
        ) {
          match.attachmentCount = pending.attachmentCount;
        }
        const idx = pendingList.findIndex(m => m.id === pending.id);
        if (idx !== -1) {
          pendingList = [
            ...pendingList.slice(0, idx),
            ...pendingList.slice(idx + 1),
          ];
          changed = true;
        }
        return false;
      }

      return true;
    });

    if (changed) {
      this.pendingMessages.set(pendingList);
      this.cdr.markForCheck();
    }
  }

  private uploadFiles(files: File[], done?: () => void): void {
    if (!files?.length) {
      done?.();
      return;
    }

    const allowed = files.filter(isAllowedDocumentAttachment);
    const rejectedCount = files.length - allowed.length;
    if (rejectedCount > 0) {
      this.attachmentFormatError.set(
        ALLOWED_DOCUMENT_ATTACHMENT_FORMAT_ERROR_MESSAGE
      );
    }
    const uploads = allowed.slice(0, 8);
    if (!uploads.length) {
      done?.();
      return;
    }
    this.attachmentFormatError.set(null);

    this.pendingUploads.update(count => count + uploads.length);
    const typeDefault = 'application/octet-stream';
    const newPendingDisplay: { name: string; type: string }[] = [];
    uploads.forEach(f => {
      const type = f.type || typeDefault;
      this.pendingFileInfos.set(f.name, { type });
      newPendingDisplay.push({ name: f.name, type });
    });
    this.pendingFilesForDisplay.update(list => [...list, ...newPendingDisplay]);

    const prs = this.userStore.currentPerson();
    const acn = this.userStore.mainCompany();
    let ks: string | null;
    if (prs?.prsId) {
      ks = `prs=${prs.prsId}`;
    } else if (acn?._id) {
      ks = `acn=${acn._id}`;
    } else {
      ks = null;
    }

    uploads.forEach(file => {
      const fd = new FormData();
      fd.append('data', file);
      fd.append('_dcs', file.name);
      fd.append('_acc', PUBLIC_ACCESS.toString());
      if (ks) fd.append('_ks', ks);

      this.documentsStore.uploadPersonalDocument(fd);
    });

    done?.();
  }

  private hasFiles(event: DragEvent): boolean {
    return Array.from(event.dataTransfer?.types ?? []).includes('Files');
  }

  private clearAllPending(): void {
    this.attachmentNamesByMessageKey.clear();
    // SD-2155: switching chats invalidates any in-flight streaming bubble
    // — it belongs to the chat we just left. Drop it so the new chat's
    // history isn't polluted.
    const hadStream = this.streamingBubble() !== null;
    if (hadStream) {
      this.streamingBubble.set(null);
    }
    if (
      !hadStream &&
      !this.pendingQueue.length &&
      !this.pendingMessages().length
    ) {
      return;
    }
    this.pendingQueue = [];
    this.pendingMessages.set([]);
    this.cdr.markForCheck();
  }
}
